import type { MentionableEntity, MentionableEntityType } from '@cobliteam/hooks/src/useSearchStore';
import diacritics from 'diacritics';

export interface MentionSearchItem {
  entity: MentionableEntity;
  entityType: MentionableEntityType;
  key: string;
  primary: string;
  secondary: string;
  searchValues: string[];
}

export interface AIMentionNodeAttributes {
  entityType: MentionableEntityType;
  id: string;
  kind: MentionableEntity['kind'];
  label: string;
}

const SELECTED_ENTITIES_PROMPT_PREFIX = '[[AI_SELECTED_ENTITIES::';
const SELECTED_ENTITIES_PROMPT_SUFFIX = '::AI_SELECTED_ENTITIES]]';
const SELECTED_ENTITIES_PROMPT_REGEX = /\n*\[\[AI_SELECTED_ENTITIES::[\s\S]*?::AI_SELECTED_ENTITIES\]\]\s*$/;

export const ALL_MENTIONABLE_ENTITY_TYPES: MentionableEntityType[] = ['vehicles', 'drivers', 'devices', 'vehicleGroups', 'geofences'];

export const normalizeMentionText = (value: string) => {
  return diacritics.remove(value).toLowerCase().trim();
};

const assertNever = (value: never): never => {
  throw new Error(`Unsupported mention entity: ${JSON.stringify(value)}`);
};

export const getMentionEntityType = (entity: MentionableEntity): MentionableEntityType => {
  switch (entity.kind) {
    case 'vehicle':
      return 'vehicles';
    case 'driver':
      return 'drivers';
    case 'device':
      return 'devices';
    case 'vehicleGroup':
      return 'vehicleGroups';
    case 'geofence':
      return 'geofences';
    default:
      return 'vehicles';
  }
};

export const getMentionEntityKey = (entity: Pick<MentionableEntity, 'id' | 'kind'>) => {
  return `${entity.kind}:${entity.id}`;
};

export const getMentionEntityKeyFromAttributes = (attributes: Pick<AIMentionNodeAttributes, 'id' | 'kind'>) => {
  return `${attributes.kind}:${attributes.id}`;
};

export const getMentionEntityPrimaryLabel = (entity: MentionableEntity) => {
  switch (entity.kind) {
    case 'vehicle':
      return entity.label || entity.licensePlate || entity.cobliId || entity.id;
    case 'driver':
      return entity.name || entity.driverCode || entity.id;
    case 'device':
      return entity.cobliId || entity.deviceModel || entity.id;
    case 'vehicleGroup':
      return entity.name || entity.id;
    case 'geofence':
      return entity.name || entity.id;
    default:
      return assertNever(entity);
  }
};

export const getMentionEntitySecondaryLabel = (entity: MentionableEntity) => {
  switch (entity.kind) {
    case 'vehicle':
      return entity.licensePlate || entity.cobliId || entity.tagsIndex || '';
    case 'driver':
      return entity.driverCode || entity.driverLicense || entity.cpf || '';
    case 'device':
      return entity.deviceModel || entity.generalType || entity.tracking?.destination?.address || '';
    case 'vehicleGroup':
      return entity.vehicles
        .map((vehicle) => vehicle.label || vehicle.licensePlate || vehicle.cobliId)
        .filter(Boolean)
        .slice(0, 2)
        .join(', ');
    case 'geofence':
      return entity.address || '';
    default:
      return '';
  }
};

export const getMentionEntityTypeLabel = (entityType: MentionableEntityType) => {
  switch (entityType) {
    case 'vehicles':
      return 'Veiculo';
    case 'drivers':
      return 'Motorista';
    case 'devices':
      return 'Dispositivo';
    case 'vehicleGroups':
      return 'Grupo';
    case 'geofences':
      return 'Cerca';
    default:
      return 'Entidade';
  }
};

export const buildMentionSearchItem = (entity: MentionableEntity): MentionSearchItem => {
  const primary = getMentionEntityPrimaryLabel(entity);
  const secondary = getMentionEntitySecondaryLabel(entity);

  const searchValues = (() => {
    switch (entity.kind) {
      case 'vehicle':
        return [entity.label, entity.licensePlate, entity.cobliId, entity.tagsIndex];
      case 'driver':
        return [entity.name, entity.driverCode, entity.driverLicense, entity.registryId, entity.cpf];
      case 'device':
        return [entity.cobliId, entity.deviceModel, entity.id, entity.tracking?.destination?.address, entity.vehicle?.alias, entity.vehicle?.licensePlate, entity.generalType];
      case 'vehicleGroup':
        return [entity.name, ...entity.vehicles.flatMap((vehicle) => [vehicle.label, vehicle.licensePlate, vehicle.cobliId])];
      case 'geofence':
        return [entity.name, entity.address];
      default:
        return [];
    }
  })().filter(Boolean) as string[];

  return {
    entity,
    entityType: getMentionEntityType(entity),
    key: getMentionEntityKey(entity),
    primary,
    secondary,
    searchValues,
  };
};

export const buildMentionNodeAttributes = (entity: MentionableEntity): AIMentionNodeAttributes => {
  return {
    id: entity.id,
    kind: entity.kind,
    label: getMentionEntityPrimaryLabel(entity),
    entityType: getMentionEntityType(entity),
  };
};

export const formatSelectedEntitiesForPrompt = (entities: MentionableEntity[]) => {
  if (!entities.length) return '';

  const payload = entities.map((entity) => ({
    entityType: getMentionEntityType(entity),
    id: entity.id,
    kind: entity.kind,
    label: getMentionEntityPrimaryLabel(entity),
  }));

  return `${SELECTED_ENTITIES_PROMPT_PREFIX}${JSON.stringify(payload)}${SELECTED_ENTITIES_PROMPT_SUFFIX}`;
};

export const stripSelectedEntitiesPromptMetadata = (value: string) => {
  return value.replace(SELECTED_ENTITIES_PROMPT_REGEX, '').trimEnd();
};
